import {
  getIndexPagination,
  getIndexPaginationSearchParams,
  normalizeIndexPagination,
  INDEX_PAGE_SIZES,
} from '@shared/lib/pagination';
import { Button, Flex } from '@mantine/core';
import { PageContainer } from '@shared/ui';
import { IconFileExport, IconPlus } from '@tabler/icons-react';
import classes from './company-list-page.module.scss';
import {
  CompanyList,
  CompanyListFilters,
  type CompanyFiltersQuery,
} from '@features/company/company-list';
import { Link, useSearchParams } from 'react-router';
import { useEffect, useState } from 'react';
import { type SortQuery, PaginateType } from '@shared/types';
import { useExportCompanies, useGetCompanies } from '@entities/company';
import { useDataExport } from '@shared/ui/modal/hooks/useDataExport';
import type { ExportDataFunction } from '@shared/ui/modal/hooks/useDataExport/types';

export const CompanyListPage = () => {
  const [filter, setFilter] = useState<CompanyFiltersQuery>({});
  const [sort, setSort] = useState<SortQuery>({ id: 'desc' });
  const [searchParams, setSearchParams] = useSearchParams();

  const { page, pageSize } = getIndexPagination(searchParams);

  useEffect(() => {
    const normalized = normalizeIndexPagination(searchParams);
    if (normalized.toString() !== searchParams.toString()) {
      setSearchParams(normalized, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const setPage = (nextPage: number) => {
    setSearchParams(
      getIndexPaginationSearchParams(searchParams, nextPage, pageSize)
    );
  };
  const setPageSize = (size: number) => {
    setSearchParams(getIndexPaginationSearchParams(searchParams, 1, size));
  };
  const { mutateAsync: exportCompanies, isPending } = useExportCompanies();

  const { openChangeDataExport } = useDataExport({
    text: 'Экспортируйте все компании или созданные в заданный период',
    title: 'Экспорт компаний',
    type: 'company',
    exportData: exportCompanies as unknown as ExportDataFunction,
    isPending,
  });

  const {
    data: companiesResponse,
    isLoading,
    isFetching,
  } = useGetCompanies({
    filter,
    sort,
    perPage: pageSize,
    page,
    paginate: PaginateType.DEFAULT,
  });

  const handleSortChange = (field: string, direction?: 'asc' | 'desc') => {
    if (!direction) {
      setSort({ id: 'desc' });
      return;
    }

    setSort({
      [field]: direction,
    });
  };

  return (
    <PageContainer
      title='Компании'
      actionButton={
        <Flex gap='0.5rem'>
          <Button
            onClick={openChangeDataExport}
            variant='default'
            leftSection={<IconFileExport />}
            size='md'
          >
            Экспорт
          </Button>
          <Button
            component={Link}
            to='/company-list/create'
            leftSection={<IconPlus />}
            className={classes.addUserButton}
            size='md'
          >
            Создать компанию
          </Button>
        </Flex>
      }
    >
      <CompanyListFilters setFilter={setFilter} pageSize={pageSize} />

      <CompanyList
        companies={companiesResponse?.data || []}
        page={page}
        setPage={setPage}
        onRecordsPerPageChange={setPageSize}
        recordsPerPageOptions={INDEX_PAGE_SIZES}
        pageSize={pageSize}
        setPageSize={setPageSize}
        isFilledFilters={Boolean(Object.keys(filter).length)}
        onSortChange={handleSortChange}
        totalRecords={companiesResponse?.pagination.total}
        currentSort={sort}
        isLoading={isLoading || isFetching}
      />
    </PageContainer>
  );
};
